<template>
  <no-auth v-if="!hasAuthorize.list"></no-auth>
  <div :class="{ 'o-card': !noCard }" v-else>
    <Table
      no-card
      i18n
      :status="getStatus"
      :title="$t(`${name}.title`)"
      :description="$t(`${name}.description`)"
      :loading="getItemsLoading"
      :items="getItems(page)"
      checkable
      :checkValue="checkedList"
      :headers="headers"
      :page="page"
      :count="getItemCount"
      :pages="getItemPages"
      :searchValue="search"
      @change:search="setSearch"
      @change:page="setChangedPage"
      @change:checkedList="checkedList = $event"
      @change:selectAllWithoutPage="SET_CHECKED_ALL"
      :searchPlaceholder="$t(`${name}.searchPlaceholder`)"
    >
      <template #filter v-if="!noFilter">
        <CycleFilter @change:filter="setFilter"></CycleFilter>
      </template>
      <template #new-button>
        <div class="u-display-flex u-width-100% u-justify-content-flex-end">
          <ExcelExport
            :fileName="`${name}-export-${date()}`"
            :fields="getExcelFields()"
            :data="checkedList.length > 0 ? checkedList : getItems()"
          >
            <template #button="{ showExcell }">
              <Button
                @click="showExcell()"
                variant="light-success"
                class="u-margin-right-xsmall"
              >
                <i class="icofont-arrow-up"></i> {{ $t("common.export") }}
              </Button>
            </template>
          </ExcelExport>
          <router-link
            v-if="hasAuthorize.add"
            :to="{ name: `new-cycle`, query: { projectId: query.projectId } }"
          >
            <Button variant="light-primary"> + {{ $t(`${name}.add`) }} </Button>
          </router-link>
        </div>
      </template>
      <template #name="{ item }">
        <div>
          <router-link
            target="_blank"
            :to="{ name: `cycle-detail`, params: { cycleId: item.id } }"
            class="u-color-primary"
          >
            <i class="u-color-primary icofont-ui-cycle"></i>
            {{ item.serialNumber }}
          </router-link>
        </div>
      </template>
      <template #project="{ item }">
        <div>
          <Chip :variant="item.projectInfo.isLocked ? 'danger' : 'success'">
            <i class="icofont-ui-lock" v-if="item.projectInfo.isLocked"></i>
            <i class="icofont-ui-unlock" v-else></i>
          </Chip>
          <span class="u-color-danger">{{ item.projectInfo.model }}</span>
        </div>
      </template>
      <template #status="{ item }">
        <div>
          {{ item.isActive }}
        </div>
      </template>
      <template #createdDate="{ item }">
        <div>
          <i class="icofont-ui-calendar"></i> {{ date(item.createdDate) }}
          <Chip :variant="item.isLocked ? 'danger' : 'success'">
            <i :class="`icofont-ui-${item.isLocked ? '' : 'un'}lock`"></i>
            {{
              item.isLocked
                ? $t("common.lockedText")
                : $t("common.unlockedText")
            }}
          </Chip>
        </div>
        <div class="u-margin-top-xsmall">
          <Chip :variant="item.isActive ? 'success' : 'danger'">
            {{ item.isActive ? $t("common.active") : $t("common.passive") }}
          </Chip>
          - <i class="icofont-user"></i> {{ item.userInfo.fullname }}
        </div>
      </template>
      <template #batteries="{ item }">
        <div
          class="u-margin-bottom-xsmall u-line-1"
          v-tooltip="{
            visible: item.evaporatorBatteryInfo?.name.length > 35,
            content: item.evaporatorBatteryInfo?.name,
          }"
        >
          <Chip
            :variant="
              item.evaporatorBatteryInfo?.isLocked ? 'danger' : 'success'
            "
          >
            <i
              class="icofont-ui-lock"
              v-if="item.evaporatorBatteryInfo?.isLocked"
            ></i>
            <i class="icofont-ui-unlock" v-else></i>
          </Chip>
          <small> {{ item.evaporatorBatteryInfo?.name }}</small>
        </div>
        <div
          class="u-line-1"
          v-tooltip="{
            visible: item.condenserBatteryInfo?.name.length > 35,
            content: item.condenserBatteryInfo?.name,
          }"
        >
          <Chip
            :variant="
              item.condenserBatteryInfo?.isLocked ? 'danger' : 'success'
            "
          >
            <i
              class="icofont-ui-lock"
              v-if="item.condenserBatteryInfo?.isLocked"
            ></i>
            <i class="icofont-ui-unlock" v-else></i>
          </Chip>
          <small> {{ item.condenserBatteryInfo?.name }}</small>
        </div>
      </template>
      <template #action="{ item }">
        <div
          class="u-display-flex u-align-items-center u-justify-content-flex-end"
        >
          <Dropdown align="right">
            <template #title>
              <div class="u-font-size-xlarge u-padding-left">
                <i class="icofont-bubble-down"></i>
              </div>
            </template>

            <template #content>
              <ul class="c-list">
                <li
                  @click="LOCKED_TO(item)"
                  v-if="hasAuthorize.locked"
                  class="c-list__item"
                  :class="{ 'c-list__item--disabled': !item.isPermission }"
                >
                  <span>
                    <i class="icofont-ui-lock u-margin-right-xsmall"></i>
                    {{ $t(`common.${item.isLocked ? "un" : ""}locked`) }}</span
                  >
                  <i class="icofont-ui-lock" v-if="!item.isPermission"></i>
                </li>
                <router-link
                  v-if="hasAuthorize.getById"
                  target="_blank"
                  :to="{ name: `cycle-detail`, params: { cycleId: item.id } }"
                >
                  <li class="c-list__item">
                    <span>
                      <i class="icofont-eye u-margin-right-xsmall"></i>
                      {{ $t("common.goToDetail") }}</span
                    >
                  </li>
                </router-link>
                <router-link
                  target="_blank"
                  :to="{
                    name: `project-detail`,
                    params: { projectId: item.projectInfo.id },
                  }"
                >
                  <li class="c-list__item">
                    <span>
                      <i class="icofont-arrow-right u-margin-right-xsmall"></i>
                      {{ $t("common.goToProject") }}</span
                    >
                  </li>
                </router-link>
                <router-link
                  v-if="hasAuthorize.add"
                  target="_blank"
                  :to="{
                    name: `new-cycle`,
                    query: { projectId: item.projectInfo.id },
                  }"
                >
                  <li class="c-list__item">
                    <span>
                      <i class="icofont-arrow-right u-margin-right-xsmall"></i>
                      {{ $t("cycle.newCycleWithProject") }}</span
                    >
                  </li>
                </router-link>
                <li
                  v-if="hasAuthorize.delete"
                  @click="REMOVE_TO(item)"
                  class="c-list__item"
                  :class="{
                    'c-list__item--disabled':
                      !item.isPermission || item.isLocked,
                  }"
                >
                  <span>
                    <i class="icofont-trash u-margin-right-xsmall"></i>
                    {{ $t("common.remove") }}</span
                  >
                  <i
                    class="icofont-ui-lock"
                    v-if="!item.isPermission || item.isLocked"
                  ></i>
                </li>
              </ul>
            </template>
          </Dropdown>
        </div>
      </template>
    </Table>
    <LockedModal
      v-model="lockedModal.status"
      :description="$t(`${update.title}.${SET_LOCKED_DESCRIPTION()}`)"
      :okText="$t(`common.${SET_LOCKED_DESCRIPTION()}`)"
      @ok="LOCKED_ITEM"
    ></LockedModal>
    <RemoveModal @ok="REMOVE_ITEM" v-model="removeModal.status"></RemoveModal>
    <drawer
      :title="$t('batteries.detail')"
      size="large"
      v-model="batteryDetailDrawer.status"
    >
      <div class="row">
        <div
          class="es-xs-12 es-sm-6"
          v-for="(battery, i) in batteryDetailDrawer.item"
          :key="i"
        >
          <BatteryDrawerDetail
            :batteryId="battery.batteryId"
          ></BatteryDrawerDetail>
        </div>
      </div>
    </drawer>
    <drawer :title="$t('admins.detail')" v-model="adminDetailDrawer.status">
      <AdminDrawerDetail
        :adminId="adminDetailDrawer.item.id"
      ></AdminDrawerDetail>
    </drawer>
  </div>
</template>

<script>
import Table from "@/components/Base/Table/index.vue";
import DateHelper from "@/helpers/date";
import { mapGetters } from "vuex";
import Button from "../Base/Button.vue";
import Chip from "../Base/Chip.vue";
import Dropdown from "../Base/Dropdown.vue";
import LockedModal from "@/components/Shared/LockedModal/index.vue";
import RemoveModal from "@/components/Shared/RemoveModal/index.vue";
import { CycleStatus } from "@/enums/Cycle/CycleStatus";
import { getCycleStatus } from "@/helpers/Cycle/getCycleStatus";
import { getBatteryType } from "@/helpers/Battery/getBatteryType";
import Drawer from "../Base/Drawer.vue";
import BatteryDrawerDetail from "../Batteries/BatteryDrawerDetail.vue";
import AdminDrawerDetail from "../Admins/AdminDrawerDetail.vue";
import Requests from "@/services/Requests";
import cycleExportFields from "@/components/Cycles/cycleExportFields";
import ExcelExport from "../Base/ExcelExport.vue";
import CycleFilter from "@/components/Cycles/CycleFilter.vue";
import authorize from "./authorize";
export default {
  components: {
    AdminDrawerDetail,
    Drawer,
    Dropdown,
    LockedModal,
    RemoveModal,
    Chip,
    BatteryDrawerDetail,
    Table,
    Button,
    ExcelExport,
    CycleFilter,
  },
  mixins: [authorize, cycleExportFields],
  props: {
    query: {
      type: Object,
      default: () => ({}),
    },
    noFilter: {
      type: Boolean,
      default: false,
    },
    noCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      name: "cycles",
      checkedList: [],
      showLockedModal: false,
      showRemoveModal: false,
      get: {
        title: "cycles",
        dispatch: "getCycles",
      },
      update: {
        title: "updateCycles",
        dispatch: "updateCycle",
        lockedStatus: CycleStatus.Locked,
      },
      remove: {
        title: "removeCycles",
        dispatch: "removeCycle",
      },
      page: 1,
      search: "",
      headers: [
        {
          col: 12,
          sm: 2,
          md: 2,
          lg: 2,
          title: "cyclesForm.seriNo",
          value: "name",
        },
        {
          col: 12,
          sm: 2,
          md: 2,
          lg: 2,
          title: "cyclesForm.project",
          value: "project",
        },
        {
          col: 12,
          sm: 3,
          md: 4,
          lg: 3,
          title: "cycles.batteries",
          value: "batteries",
        },
        {
          col: 6,
          sm: 3,
          md: 3,
          lg: 3,
          title: "common.status",
          value: "createdDate",
        },
        {
          col: 2,
          sm: 1,
          md: 4,
          lg: 1,
          align: "right",
          title: "",
          value: "action",
        },
      ],
      removeModal: { status: false, item: {} },
      lockedModal: { status: false, item: {} },
      batteryDetailDrawer: { status: false, item: {} },
      adminDetailDrawer: { status: false, item: {} },
      filter: {},
    };
  },
  computed: {
    ...mapGetters({
      getItems: "getCycles",
      getItemsLoading: "getCyclesLoading",
      getItemPages: "getCyclePages",
      getItemCount: "getCycleCount",
      getActiveUser: "getActiveUser",
      getStatus: "getCycleStatus",
    }),
  },
  methods: {
    getBatteryType,
    getCycleStatus,
    setFilter(filter) {
      this.page = 1;
      const filterScoped = {
        isLocked:
          filter.isLocked == null
            ? ""
            : filter.isLocked
            ? "true"
            : "false" || "",
        userId: filter.user?.id || "",
        projectId: filter.projectId?.id || "",
      };
      // remove
      this.filter = filterScoped;
      this.getItemsOfPage();
    },
    async SET_CHECKED_ALL(data) {
      if (data) {
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: true,
          status: true,
        });
        const response = await Requests.Cycle.GetAll({
          query: { page: 1, limit: this.getItemCount, search: this.search },
        });
        if (response.message === "OK") {
          this.checkedList = [...response.data.items];
        }
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: false,
        });
      }
    },
    date(value = new Date()) {
      const date = new DateHelper(this.$i18n.locale);
      return date.format(value);
    },
    SET_LOCKED_DESCRIPTION() {
      return this.lockedModal.item.isLocked ? "unlocked" : "locked";
    },
    REMOVE_TO(item) {
      this.removeModal = {
        status: true,
        item: item,
      };
    },
    LOCKED_TO(item) {
      this.lockedModal = {
        status: true,
        item: item,
      };
    },
    async REMOVE_ITEM(loading) {
      loading(true);

      const response = await this.$store.dispatch(this.remove.dispatch, {
        id: this.removeModal.item.id,
      });
      if (response.message === "OK") {
        this.getItemsOfPage();
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.remove.title}.success`),
          variant: "success",
          duration: 2500,
        });
      } else {
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.remove.title}.error`),
          variant: "danger",
          duration: 2500,
        });
      }
      loading(false);
    },
    async LOCKED_ITEM(loading) {
      loading(true);
      const response = await Requests.Cycle.UpdateLockStatus(
        { params: { id: this.lockedModal.item.id } },
        { isLocked: !this.lockedModal.item.isLocked }
      );
      if (response.message === "OK") {
        this.getItemsOfPage();
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.update.title}.success`),
          variant: "success",
          duration: 2500,
        });
      } else {
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.update.title}.error`),
          variant: "danger",
          duration: 2500,
        });
      }
      loading(false);
    },
    setQuery() {
      const page = Number(this.$route.query.page);
      const search = this.$route.query.search;
      if (!isNaN(page)) this.page = page;
      if (search) this.search = search;
      this.getItemsOfPage();
    },
    getItemsOfPage() {
      this.removeModal = { status: false, item: {} };
      this.lockedModal = { status: false, item: {} };
      this.$store.dispatch(this.get.dispatch, {
        query: {
          limit: 10,
          page: this.page,
          search: this.search,
          ...this.filter,
          ...this.query,
        },
        force: true,
      });
    },
    setSearch(search) {
      this.search = search;
      this.page = 1;
      this.$router.replace({
        query: { ...this.$route.query, search: this.search, page: 1 },
      });
      this.getItemsOfPage();
    },
    setChangedPage(page) {
      this.$router.replace({
        query: { ...this.$route.query, page: page },
      });
      this.page = page;
      this.getItemsOfPage();
    },
    showBatteryDetail(batteries) {
      this.batteryDetailDrawer = { status: true, item: batteries };
    },
    showAdminDetail(userInfo) {
      this.adminDetailDrawer = { status: true, item: userInfo };
    },
  },
  mounted() {
    this.setQuery();
  },
};
</script>

<style></style>
